<template>
  <canvas v-if="season" ref="canvas" class="fixed inset-0 z-[995] w-full h-full pointer-events-none" aria-hidden="true" />
</template>

<script setup>
// One-off New Year moment in the site's dot-matrix style, over a dimmed page (SeasonSnow adds
// the Gregorian theme's ambient snowfall):
//   Gregorian: frost-blue dots gather into last year (2026), the changed digits morph into the new
//              year (2027), a caption fades in, then the number bursts into fireworks.
//   Hijri:     the dots form a crescent that morphs into the new year (1449), then drift
//              down as falling stars.
// It plays once per session after the page is idle, and again from the greeting's replay
// button. Any click, key or scroll skips it; the canvas never takes pointer events.
const season = useSeason()
const replayCount = useState('seasonReplay', () => 0)
const { t, locale } = useI18n()
const canvas = ref(null)

const SESSION_KEY = 'season_played'
const GRAVITY = 0.045
const DRAG = 0.985

// Timeline, in ms from the start.
const T = {
  scrimIn: 500,
  gatherAt: 250,
  gatherFor: 1300,
  morphAt: 2500,
  morphFor: 900,
  captionAt: 3100,
  releaseAt: 4500,
  scrimOut: 900,
  showEnd: 8000, // last fireworks / stars are scheduled before this
}
const SCRIM_ALPHA = 0.93

let ctx, width, height, raf = 0, startedAt = 0
let dots = [] // the dot-matrix particles that form the numbers
let particles = [] // free particles: rockets, sparks, stars
let schedule = []
let released = false
let morphed = false
let skipAt = 0 // set when the viewer skips; everything fades from then
let skipScrim = 0 // scrim opacity at that moment
let caption = ''
let layout = null
let palette = []

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
const rand = (min, max) => min + Math.random() * (max - min)
const pick = (list) => list[Math.floor(Math.random() * list.length)]
const clamp01 = (x) => Math.max(0, Math.min(1, x))
const easeInOut = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2)
const easeOut = (x) => 1 - Math.pow(1 - x, 3)

// The theme accent plus white and a highlight: ice blue for the cold Gregorian theme,
// warm gold for Hijri.
function readPalette() {
  const accent = getComputedStyle(document.documentElement).getPropertyValue('--primary-rgb').trim() || '200 225 255'
  const highlight = season.value.name === 'gregorian' ? '150 195 255' : '255 236 190'
  palette = [accent, accent, '255 255 255', highlight]
}

function resize() {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  width = window.innerWidth
  height = window.innerHeight
  canvas.value.width = width * dpr
  canvas.value.height = height * dpr
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
}

/* ---------- shapes: sample drawn glyphs into grid points ---------- */

const DISPLAY_FONT = 'pp-extraBold'

// Draws with `paint` on an offscreen canvas and returns the lit cells of a `gap` grid.
function sample(paint, gap) {
  const off = document.createElement('canvas')
  off.width = width
  off.height = height
  const o = off.getContext('2d', { willReadFrequently: true })
  o.fillStyle = '#fff'
  paint(o)
  const data = o.getImageData(0, 0, width, height).data
  const points = []
  for (let y = 0; y < height; y += gap) {
    for (let x = 0; x < width; x += gap) {
      if (data[(y * width + x) * 4 + 3] > 128) points.push({ x, y })
    }
  }
  return points
}

// One point group per character, all laid out from the same left edge, so characters that do
// not change between the two strings sample to identical points and their dots stay put.
function sampleString(text, fontSize, left, baseline, gap) {
  const o = document.createElement('canvas').getContext('2d')
  o.font = `800 ${fontSize}px ${DISPLAY_FONT}`
  return [...text].map((char, i) => {
    const x = left + o.measureText(text.slice(0, i)).width
    return sample((c) => {
      c.font = `800 ${fontSize}px ${DISPLAY_FONT}`
      c.textBaseline = 'middle'
      c.fillText(char, x, baseline)
    }, gap)
  })
}

function sampleCrescent(cx, cy, r, gap) {
  return sample((c) => {
    c.beginPath()
    c.arc(cx, cy, r, 0, Math.PI * 2)
    c.fill()
    c.globalCompositeOperation = 'destination-out'
    c.beginPath()
    c.arc(cx + r * 0.42, cy - r * 0.18, r * 0.86, 0, Math.PI * 2)
    c.fill()
    c.globalCompositeOperation = 'source-over'
    // a small star inside the curve
    const sx = cx + r * 0.62, sy = cy - r * 0.42, s = r * 0.16
    c.beginPath()
    for (let i = 0; i < 10; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 5
      const rr = i % 2 ? s * 0.45 : s
      c.lineTo(sx + Math.cos(a) * rr, sy + Math.sin(a) * rr)
    }
    c.fill()
  }, gap)
}

// Pair every point of shape A with one of shape B, group by group, padding the smaller side by
// repeating its points. Sorting both the same way keeps the morph coherent instead of a jumble.
function buildDots(groupsA, groupsB) {
  const order = (p) => p.x + p.y * 0.35
  dots = []
  groupsA.forEach((a, g) => {
    const b = groupsB[g]
    if (!a.length || !b.length) return
    const same = a.length === b.length && a.every((p, i) => p.x === b[i].x && p.y === b[i].y)
    const sa = [...a].sort((p, q) => order(p) - order(q))
    const sb = same ? sa : [...b].sort((p, q) => order(p) - order(q))
    const n = Math.max(sa.length, sb.length)
    for (let i = 0; i < n; i++) {
      const pa = sa[Math.floor((i * sa.length) / n)]
      const pb = sb[Math.floor((i * sb.length) / n)]
      dots.push({
        x: rand(0, width),
        y: rand(0, height),
        ax: pa.x, ay: pa.y,
        bx: pb.x, by: pb.y,
        moves: !same,
        delay: rand(0, 450),
        phase: rand(0, Math.PI * 2),
        color: Math.random() < 0.85 ? palette[0] : pick(palette.slice(2)),
        alpha: 0,
      })
    }
  })
  for (const d of dots) {
    d.sx = d.x
    d.sy = d.y
  }
}

function buildLayout() {
  const { name, year } = season.value
  const mobile = width < 640
  const gap = mobile ? 5 : 7
  const cy = height * 0.44
  const next = String(year)

  // Fit the number to ~78% of the width and ~30% of the height.
  const o = document.createElement('canvas').getContext('2d')
  o.font = `800 100px ${DISPLAY_FONT}`
  const fontSize = Math.min((width * 0.78 * 100) / o.measureText(next).width, height * 0.3)
  o.font = `800 ${fontSize}px ${DISPLAY_FONT}`
  const textWidth = o.measureText(next).width
  const left = (width - textWidth) / 2

  const groupsB = sampleString(next, fontSize, left, cy, gap)
  let groupsA
  if (name === 'gregorian' && String(year - 1).length === next.length) {
    groupsA = sampleString(String(year - 1), fontSize, left, cy, gap)
  } else {
    // Hijri (or a digit-count change): one group, the whole shape morphs into the number.
    groupsA = [sampleCrescent(width / 2, cy, Math.min(fontSize * 0.8, width * 0.3), gap)]
    groupsB.splice(0, groupsB.length, groupsB.flat())
  }
  buildDots(groupsA, groupsB)

  layout = { cy, fontSize, gap, dotSize: gap * 0.62, captionY: cy + fontSize * 0.5 + (mobile ? 36 : 56) }
}

/* ---------- free particles: fireworks and stars ---------- */

function launchRocket(x = rand(width * 0.15, width * 0.85)) {
  particles.push({
    kind: 'rocket',
    x,
    y: height,
    vx: rand(-0.6, 0.6),
    vy: -rand(height / 75, height / 60),
    targetY: rand(height * 0.12, height * 0.45),
    color: pick(palette),
  })
}

function burst(x, y, color) {
  const count = width < 640 ? 60 : 110
  const power = rand(0.8, 1.25)
  // a brief flash of light where the shell opens
  particles.push({ kind: 'flash', x, y, r: (width < 640 ? 70 : 130) * power, life: 1, color })
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + rand(-0.08, 0.08)
    const speed = rand(2, 6) * power
    const p = spark(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed, Math.random() < 0.75 ? color : pick(palette), rand(1.6, 3))
    p.decay = rand(0.006, 0.012)
    p.glitter = Math.random() < 0.3
    particles.push(p)
  }
}

// Three shells at once when the number lets go.
function finale() {
  launchRocket(width * 0.25)
  launchRocket(width * 0.5)
  launchRocket(width * 0.75)
}

const spark = (x, y, vx, vy, color, size = rand(1.2, 2.4)) => ({ kind: 'spark', x, y, vx, vy, life: 1, decay: rand(0.008, 0.016), size, color })

function dropStar() {
  particles.push({
    kind: 'star',
    x: rand(0, width),
    y: rand(-40, -10),
    vx: rand(-0.25, 0.25),
    vy: rand(0.6, 1.4),
    size: rand(3, 7),
    phase: rand(0, Math.PI * 2),
    life: 1,
    decay: 0,
    color: pick(palette),
  })
}

function drawStar(x, y, s) {
  ctx.beginPath()
  ctx.moveTo(x, y - s)
  ctx.quadraticCurveTo(x, y, x + s, y)
  ctx.quadraticCurveTo(x, y, x, y + s)
  ctx.quadraticCurveTo(x, y, x - s, y)
  ctx.quadraticCurveTo(x, y, x, y - s)
  ctx.fill()
}

// The formed number lets go: sparks flying out for the Gregorian year, falling stars for Hijri.
function release() {
  released = true
  const cx = width / 2
  const gregorian = season.value.name === 'gregorian'
  for (const d of dots) {
    if (gregorian) {
      const dx = d.x - cx, dy = d.y - layout.cy
      const dist = Math.hypot(dx, dy) || 1
      const speed = rand(2, 7)
      const p = spark(d.x, d.y, (dx / dist) * speed + rand(-1, 1), (dy / dist) * speed - 1.5, d.color, layout.dotSize * 0.6)
      p.decay = rand(0.01, 0.022)
      particles.push(p)
    } else {
      particles.push({ kind: 'star', x: d.x, y: d.y, vx: rand(-0.4, 0.4), vy: rand(0.2, 1.1), size: rand(1.5, 3.5), phase: d.phase, life: 1, decay: rand(0.004, 0.009), color: d.color })
    }
  }
  dots = []
}

/* ---------- frame ---------- */

function scrimAlpha(elapsed) {
  if (skipAt) return skipScrim * (1 - clamp01((elapsed - skipAt) / 300))
  const fadeIn = clamp01(elapsed / T.scrimIn)
  const fadeOut = 1 - clamp01((elapsed - T.releaseAt) / T.scrimOut)
  return SCRIM_ALPHA * Math.min(fadeIn, fadeOut)
}

function frame(now) {
  const elapsed = now - startedAt
  while (schedule.length && schedule[0].at <= elapsed) schedule.shift().run()
  if (!released && !skipAt && elapsed >= T.releaseAt) release()
  if (!morphed && elapsed >= T.morphAt) {
    morphed = true
    for (const d of dots) {
      d.sx = d.x
      d.sy = d.y
      d.delay = d.moves ? rand(0, 250) : 0
    }
  }
  // After a skip, everything fades out over 300ms.
  const fade = skipAt ? 1 - clamp01((elapsed - skipAt) / 300) : 1

  ctx.globalCompositeOperation = 'source-over'
  ctx.clearRect(0, 0, width, height)

  const scrim = scrimAlpha(elapsed)
  if (scrim > 0) {
    ctx.fillStyle = `rgba(20, 20, 28, ${scrim})`
    ctx.fillRect(0, 0, width, height)
    // soft gold glow behind the number, strongest as the new year lands
    const pulse = morphed ? 1 - clamp01((elapsed - T.morphAt - T.morphFor) / 1200) * 0.5 : 0.5
    const glow = ctx.createRadialGradient(width / 2, layout.cy, 0, width / 2, layout.cy, Math.max(width, height) * 0.5)
    glow.addColorStop(0, `rgb(${palette[0]} / ${0.16 * pulse * (scrim / SCRIM_ALPHA)})`)
    glow.addColorStop(1, 'rgb(0 0 0 / 0)')
    ctx.fillStyle = glow
    ctx.fillRect(0, 0, width, height)
  }

  ctx.globalCompositeOperation = 'lighter'

  // number dots: gather into shape A, morph to shape B
  const s = layout.dotSize
  for (const d of dots) {
    let tx, ty, k, wobble = 0
    if (!morphed) {
      k = easeInOut(clamp01((elapsed - T.gatherAt - d.delay) / T.gatherFor))
      tx = d.ax
      ty = d.ay
    } else {
      k = d.moves ? easeInOut(clamp01((elapsed - T.morphAt - d.delay) / T.morphFor)) : 1
      tx = d.bx
      ty = d.by
      // moving dots arc slightly on the way, so the digit visibly rolls over
      if (d.moves) wobble = Math.sin(Math.PI * k) * layout.fontSize * 0.12
    }
    d.x = d.sx + (tx - d.sx) * k
    d.y = d.sy + (ty - d.sy) * k - wobble
    d.alpha = Math.min(1, d.alpha + 0.04)
    const twinkle = 0.78 + 0.22 * Math.sin(d.phase + elapsed / 180)
    ctx.fillStyle = `rgb(${d.color} / ${d.alpha * twinkle * fade})`
    ctx.fillRect(d.x - s / 2, d.y - s / 2, s, s)
  }

  // caption under the number
  if (caption && elapsed < T.releaseAt + 400) {
    const a = easeOut(clamp01((elapsed - T.captionAt) / 600)) * (1 - clamp01((elapsed - T.releaseAt) / 400)) * fade
    if (a > 0) {
      const rtl = locale.value === 'ar'
      const size = Math.max(14, Math.min(26, width * 0.028))
      ctx.globalCompositeOperation = 'source-over'
      ctx.font = rtl ? `600 ${size}px "Readex Pro", Tahoma, sans-serif` : `700 ${size}px Cinetype-bold, "Cinetype-bold fallback", sans-serif`
      if ('letterSpacing' in ctx) ctx.letterSpacing = rtl ? '0px' : `${size * 0.35}px`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = `rgb(${palette[0]} / ${a})`
      ctx.fillText(caption, width / 2, layout.captionY + (1 - a) * 12)
      if ('letterSpacing' in ctx) ctx.letterSpacing = '0px'
      ctx.globalCompositeOperation = 'lighter'
    }
  }

  // free particles
  const alive = []
  for (const p of particles) {
    if (p.kind === 'rocket') {
      p.x += p.vx
      p.y += p.vy
      p.vy += GRAVITY
      if (p.y <= p.targetY || p.vy >= 0) {
        burst(p.x, p.y, p.color)
        continue
      }
      ctx.fillStyle = `rgb(${p.color} / ${0.9 * fade})`
      ctx.fillRect(p.x - 1, p.y, 2, 8)
    } else if (p.kind === 'spark') {
      p.vx *= DRAG
      p.vy = p.vy * DRAG + GRAVITY
      p.x += p.vx
      p.y += p.vy
      p.life -= p.decay
      if (p.life <= 0) continue
      // glitter sparks flicker as they fall
      const flicker = p.glitter && Math.random() < 0.5 ? 0.25 : 1
      ctx.fillStyle = `rgb(${p.color} / ${p.life * flicker * fade})`
      ctx.beginPath()
      ctx.arc(p.x, p.y, p.size * (0.5 + p.life / 2), 0, Math.PI * 2)
      ctx.fill()
    } else if (p.kind === 'flash') {
      p.life -= 0.07
      if (p.life <= 0) continue
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r)
      g.addColorStop(0, `rgb(${p.color} / ${0.35 * p.life * fade})`)
      g.addColorStop(1, 'rgb(0 0 0 / 0)')
      ctx.fillStyle = g
      ctx.fillRect(p.x - p.r, p.y - p.r, p.r * 2, p.r * 2)
    } else {
      p.x += p.vx
      p.y += p.vy
      p.life -= p.decay
      if (elapsed > T.showEnd - 1500) p.life -= 0.02
      const edge = Math.min(1, (height - p.y) / (height * 0.25))
      if (p.life <= 0 || edge <= 0) continue
      const twinkle = 0.55 + 0.45 * Math.sin(p.phase + elapsed / 250)
      ctx.fillStyle = `rgb(${p.color} / ${twinkle * edge * p.life * fade})`
      drawStar(p.x, p.y, p.size)
    }
    alive.push(p)
  }
  particles = alive

  const skipDone = skipAt && elapsed - skipAt > 320
  if (!skipDone && (dots.length || particles.length || schedule.length)) raf = requestAnimationFrame(frame)
  else stop()
}

/* ---------- play / skip ---------- */

const SKIP_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchmove']
function skip() {
  if (!raf || skipAt) return
  const elapsed = performance.now() - startedAt
  skipScrim = scrimAlpha(elapsed)
  skipAt = elapsed
  schedule = []
}

async function play() {
  if (!season.value || !canvas.value || reducedMotion()) return
  stop()
  ctx = canvas.value.getContext('2d')
  readPalette()
  resize()
  // The display face is not preloaded on every page; wait for it (briefly) before sampling glyphs.
  await Promise.race([document.fonts.load(`800 100px ${DISPLAY_FONT}`), new Promise((r) => setTimeout(r, 1500))]).catch(() => {})
  if (!canvas.value) return

  buildLayout()
  caption = t(`season.${season.value.name}.kicker`)
  caption = locale.value === 'ar' ? caption : caption.toUpperCase()
  released = false
  morphed = false
  skipAt = 0
  particles = []
  schedule = []

  if (season.value.name === 'gregorian') {
    schedule.push({ at: T.releaseAt, run: finale })
    for (let at = T.releaseAt + 700; at < T.showEnd - 1500; at += rand(220, 420)) {
      schedule.push({ at, run: () => launchRocket() })
      if (Math.random() < 0.35) schedule.push({ at: at + 60, run: () => launchRocket() })
    }
  } else {
    const count = width < 640 ? 35 : 70
    for (let i = 0; i < count; i++) schedule.push({ at: T.releaseAt + (i / count) * (T.showEnd - T.releaseAt - 2500), run: dropStar })
  }

  window.addEventListener('resize', skip)
  SKIP_EVENTS.forEach((e) => window.addEventListener(e, skip, { passive: true }))
  startedAt = performance.now()
  raf = requestAnimationFrame(frame)
}

function stop() {
  cancelAnimationFrame(raf)
  raf = 0
  dots = []
  particles = []
  schedule = []
  window.removeEventListener('resize', skip)
  SKIP_EVENTS.forEach((e) => window.removeEventListener(e, skip))
  ctx?.clearRect(0, 0, width, height)
}

// The replay button's own click would count as a skip, so start on the next frame.
watch(replayCount, () => requestAnimationFrame(() => play()))

// The season is resolved on the client after hydration (app.vue), so wait for it.
watch(season, (value) => {
  if (!value) return
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return
    sessionStorage.setItem(SESSION_KEY, '1')
  } catch {}
  // After load and idle, so it never competes with the hero image and fonts.
  const start = () => onIdle(play, 3000)
  if (document.readyState === 'complete') start()
  else window.addEventListener('load', start, { once: true })
}, { immediate: true })

onBeforeUnmount(stop)
</script>
